import React, { useState, useEffect } from "react";
import { X, Tag, Plus, Loader2 } from "lucide-react";
import { FilesystemNode } from "../../api/listFolderContents/types";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  listTagsAction,
  assignTagToNodeAction,
  createAndAssignTagAction,
  removeTagFromNodeAction,
} from "../../slices/fileManagementTagSlice";
import {
  addTagToNode,
  assignTagToNode,
  removeTagFromNode,
} from "../../slices/fileManagementSlice";
import { useNotification } from "@/context/NotificationContext";
import styles from "./TagSidebar.module.css";

interface TagSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  node: FilesystemNode | null;
  onNodeUpdate: (updatedNode: FilesystemNode) => void;
  isMobile: boolean;
}

const colorOptions = [
  { name: "Red", value: "#ef4444" },
  { name: "Orange", value: "#f97316" },
  { name: "Amber", value: "#f59e0b" },
  { name: "Yellow", value: "#eab308" },
  { name: "Lime", value: "#84cc16" },
  { name: "Green", value: "#22c55e" },
  { name: "Emerald", value: "#10b981" },
  { name: "Teal", value: "#14b8a6" },
  { name: "Cyan", value: "#06b6d4" },
  { name: "Sky", value: "#0ea5e9" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Indigo", value: "#6366f1" },
  { name: "Violet", value: "#8b5cf6" },
  { name: "Purple", value: "#a855f7" },
  { name: "Fuchsia", value: "#d946ef" },
  { name: "Pink", value: "#ec4899" },
  { name: "Rose", value: "#f43f5e" },
  { name: "Gray", value: "#6b7280" },
];

const TagSidebar: React.FC<TagSidebarProps> = ({
  isOpen,
  onClose,
  node,
  onNodeUpdate,
  isMobile,
}) => {
  const [newTagName, setNewTagName] = useState("");
  const [selectedTagColor, setSelectedTagColor] = useState(
    colorOptions[10].value,
  ); // Default to Blue
  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  // Get tag state from Redux store
  const { tags: availableTags, listTagsLoading } = useAppSelector(
    (state: RootState) => state.fileManagementTag,
  );

  // Load available tags when sidebar opens
  useEffect(() => {
    if (isOpen && node) {
      dispatch(listTagsAction());
      // Reset form state
      setNewTagName("");
      setTagSearchTerm("");
      setSelectedTagColor(colorOptions[10].value); // Reset to Blue
    }
  }, [isOpen, node, dispatch]);

  if (!node) return null;

  const handleCreateAndAssignTag = async () => {
    if (!node || !newTagName.trim()) return;

    try {
      const response = await dispatch(
        createAndAssignTagAction({
          filesystemNodeUuid: node.uuid,
          name: newTagName,
          color: selectedTagColor,
        }),
      );

      if (response && response.data) {
        // Use the actual tag data from the response
        const newTag = {
          id: response.data.id,
          name: response.data.name,
          color: response.data.color,
        };

        // Update Redux state globally
        dispatch(addTagToNode(response.data));

        // Update the local node for immediate UI feedback
        onNodeUpdate({
          ...node,
          tags: [...(node.tags || []), newTag],
        });

        setNewTagName("");
      }
      // No toast notification - silent success
    } catch (error) {
      // Only show notification for errors
      showNotification("Error", "Failed to create tag", "error");
    }
  };

  const handleAssignExistingTag = async (tagId: number) => {
    if (!node) return;

    try {
      const response = await dispatch(
        assignTagToNodeAction({
          tagId,
          filesystemNodeUuid: node.uuid,
        }),
      );

      if (response && response.data) {
        // Update Redux state globally
        dispatch(assignTagToNode(response.data));

        // Also update local node for immediate UI feedback
        const tag = availableTags.find((t) => t.id === tagId);
        if (tag) {
          onNodeUpdate({
            ...node,
            tags: [...(node.tags || []), tag],
          });
        }
        // No toast notification - silent success
      }
    } catch (error) {
      // Only show notification for errors
      showNotification("Error", "Failed to assign tag", "error");
    }
  };

  const handleRemoveTag = async (tagId: number) => {
    if (!node) return;

    try {
      await dispatch(
        removeTagFromNodeAction({
          tagId,
          filesystemNodeUuid: node.uuid,
        }),
      );

      // Update Redux state globally
      dispatch(
        removeTagFromNode({
          tagId,
          filesystemNodeUuid: node.uuid,
        }),
      );

      // Update local node for immediate UI feedback
      onNodeUpdate({
        ...node,
        tags: node.tags?.filter((t) => t.id !== tagId) || [],
      });
      // No toast notification - silent success
    } catch (error) {
      // Only show notification for errors
      showNotification("Error", "Failed to remove tag", "error");
    }
  };

  const panelClasses = [
    styles.tagSidebar,
    isOpen ? styles.open : styles.closed,
    isMobile ? styles.mobilePanel : styles.desktopPanel,
  ].join(" ");

  return (
    <>
      {/* Backdrop for mobile only */}
      {isOpen && isMobile && (
        <div className={styles.backdrop} onClick={onClose} />
      )}

      {/* Sidebar panel */}
      <div className={panelClasses}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            <Tag className={styles.titleIcon} size={16} />
            Manage Tags
          </h3>
          <button
            aria-label="Close"
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <X className={styles.closeIcon} />
          </button>
        </div>

        {/* File name section */}
        <div className={styles.fileNameSection}>
          <div className={styles.fileLabel}>File</div>
          <div className={styles.fileName}>{node.name}</div>
        </div>

        {/* Content */}
        <div className={styles.body}>
          <div className={styles.content}>
            {/* Current Tags */}
            {node.tags && node.tags.length > 0 && (
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>Current Tags</h4>
                <div className={styles.tagsList}>
                  {node.tags.map((tag) => (
                    <span
                      className={styles.currentTag}
                      key={tag.id}
                      style={{
                        backgroundColor: tag.color
                          ? `${tag.color}20`
                          : "#3b82f620",
                        color: tag.color || "#3b82f6",
                        borderColor: tag.color ? `${tag.color}40` : "#3b82f640",
                      }}
                    >
                      {tag.name}
                      <button
                        className={styles.removeTagButton}
                        onClick={() => handleRemoveTag(tag.id)}
                        title="Remove tag"
                      >
                        <X className={styles.removeIcon} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Tag */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Create New Tag</h4>
              <div className={styles.createTagForm}>
                <input
                  className={styles.tagInput}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleCreateAndAssignTag();
                    }
                  }}
                  placeholder="Enter tag name..."
                  type="text"
                  value={newTagName}
                />

                {/* Color palette */}
                <div className={styles.colorPaletteContainer}>
                  <div className={styles.colorPalette}>
                    {colorOptions.map((color) => (
                      <button
                        aria-label={`Select ${color.name} color`}
                        className={`${styles.colorOption} ${
                          selectedTagColor === color.value
                            ? styles.selectedColor
                            : ""
                        }`}
                        key={color.value}
                        onClick={() => setSelectedTagColor(color.value)}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                        type="button"
                      />
                    ))}
                  </div>
                </div>

                <button
                  className={styles.createButton}
                  disabled={!newTagName.trim()}
                  onClick={handleCreateAndAssignTag}
                >
                  <Plus className={styles.buttonIcon} />
                  Create & Assign
                </button>
              </div>
            </div>

            {/* Existing Tags */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Available Tags</h4>
              <input
                className={styles.searchInput}
                onChange={(e) => setTagSearchTerm(e.target.value)}
                placeholder="Search tags..."
                type="text"
                value={tagSearchTerm}
              />

              {listTagsLoading ? (
                <div className={styles.loadingContainer}>
                  <Loader2 className={styles.spinningIcon} size={24} />
                  <span>Loading tags...</span>
                </div>
              ) : (
                <div className={styles.availableTagsContainer}>
                  {availableTags
                    .filter(
                      (tag) =>
                        tag.name
                          .toLowerCase()
                          .includes(tagSearchTerm.toLowerCase()) &&
                        !node.tags?.some((t) => t.id === tag.id),
                    )
                    .map((tag) => (
                      <button
                        className={styles.availableTag}
                        key={tag.id}
                        onClick={() => handleAssignExistingTag(tag.id)}
                        style={{
                          backgroundColor: tag.color
                            ? `${tag.color}20`
                            : "#3b82f620",
                          color: tag.color || "#3b82f6",
                          borderColor: tag.color
                            ? `${tag.color}40`
                            : "#3b82f640",
                        }}
                      >
                        <Plus className={styles.addIcon} />
                        {tag.name}
                      </button>
                    ))}
                  {availableTags.filter(
                    (tag) =>
                      tag.name
                        .toLowerCase()
                        .includes(tagSearchTerm.toLowerCase()) &&
                      !node.tags?.some((t) => t.id === tag.id),
                  ).length === 0 && (
                    <div className={styles.emptyState}>
                      {tagSearchTerm
                        ? "No matching tags found"
                        : "No available tags"}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TagSidebar;
